import { env } from "@/env";
import { toast } from "@/app/_providers/toast";
import { createVideoThumbnail, getMediaDuration } from "@/app/_utilities/videoUtils";
import { logger } from "@utilities/logger";
import { useCallback, useState } from "react";
import { poll } from "@utilities/polling";
import { simpleStringHash } from "@/app/_utilities/hash";
import { MusicColorScheme } from "@ui/components/routes/music/colorScheme";
import { useHerotranslateMediaTranslations } from "./HeroTranslateMediaTranslations";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { getErrorMessageFromUnknownError } from "@utilities/errors";

const SERVER_URL = env.NEXT_PUBLIC_XI_SERVER_URL;

export type MediaInfo = {
  videoUrl: string;
  color?: MusicColorScheme;
};

export type TranslateMode = "audio" | "video";

export interface useHeroTranslateAudioProps {
  originalVideo: MediaInfo;
  translatedVideo: MediaInfo;
  initialMode?: TranslateMode;
}

interface DubbingResponse {
  dubbing_id: string;
  expected_duration_sec: number;
}

export type TranslateAudioState =
  | {
      type: "demo";
      mode: TranslateMode;
      originalVideo: MediaInfo;
      translatedVideo: MediaInfo;
    }
  | {
      type: "uploaded";
      mode: TranslateMode;
      file: File;
      fileThumbnail: string;
      originalVideo: MediaInfo;
    }
  | {
      type: "generating";
      mode: TranslateMode;
      file: File;
      fileThumbnail: string;
      originalVideo: MediaInfo;
    }
  | {
      type: "complete";
      mode: TranslateMode;
      file: File;
      fileThumbnail: string;
      originalVideo: MediaInfo;
      translatedVideo: MediaInfo;
      dubbing_id: string;
    }
  | {
      type: "quota_exceeded";
      mode: TranslateMode;
      originalVideo: MediaInfo;
      file: File;
      fileThumbnail: string;
    }
  | {
      type: "duration_exceeded";
      mode: TranslateMode;
      originalVideo: MediaInfo;
      file: File;
      fileThumbnail: string;
    };

const schemes: MusicColorScheme[] = [
  MusicColorScheme.red,
  MusicColorScheme.blue,
  MusicColorScheme.purple,
  MusicColorScheme.cyan,
  MusicColorScheme.orange,
  MusicColorScheme.green,
  MusicColorScheme.black,
];
const pickColorSchemeFromSeed = (seed: string): MusicColorScheme => {
  const hash = simpleStringHash(seed);
  const index = Math.abs(hash) % schemes.length;
  return schemes[index];
};

export const MAX_UPLOAD_DURATION_MINUTES = 1;

export const MAX_UPLOAD_SIZE_MB = 50;
export const BYTES_PER_MB = 1024 * 1024;
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * BYTES_PER_MB;

export function useHeroTranslateAudio({
  originalVideo,
  translatedVideo,
  initialMode = "video", // this is just so for the demo we always expect videos. We don't accept audio for the demo
}: useHeroTranslateAudioProps) {
  const [state, setState] = useState<TranslateAudioState>({
    type: "demo",
    mode: initialMode,
    originalVideo,
    translatedVideo,
  });
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const t = useHerotranslateMediaTranslations();

  const uploadVideo = useCallback(
    async (file: File) => {
      try {
        const isAudio = file.type.startsWith("audio/");
        const detectedMode: TranslateMode = isAudio ? "audio" : "video";

        // Create object URL for the uploaded file (needed for display)
        const videoUrl = URL.createObjectURL(file);
        // Create thumbnail from video or use a generic image for audio
        const fileThumbnail = isAudio ? "/placeholder-1x1.jpg" : await createVideoThumbnail(file);
        if (selectedMediaIndex !== 0) setSelectedMediaIndex(0);
        const originalVideo = {
          videoUrl,
          color: detectedMode === "audio" ? pickColorSchemeFromSeed(file.name) : undefined,
        };
        // Check duration before proceeding
        try {
          const durationSeconds = await getMediaDuration(file);
          const durationMinutes = durationSeconds / 60;
          if (durationMinutes > MAX_UPLOAD_DURATION_MINUTES) {
            setState({
              type: "duration_exceeded",
              mode: detectedMode,
              file,
              fileThumbnail,
              originalVideo,
            });
            captureEvent(EventName.mw_translate_media_limit_shown, {
              limit_type: "duration",
              translate_media_mode: detectedMode,
              max_duration_minutes: MAX_UPLOAD_DURATION_MINUTES,
            });
            return;
          }
        } catch (durationError) {
          // If we can't determine duration, proceed anyway (some files may not have duration metadata)
          logger.warn("Could not determine media duration:", durationError);
        }

        setState({
          type: "uploaded",
          mode: detectedMode,
          file,
          fileThumbnail,
          originalVideo,
        });
        captureEvent(EventName.mw_translate_media_file_uploaded, {
          translate_media_mode: detectedMode,
          file_size_bytes: file.size,
        });
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to create thumbnail");
        toast.error(t("widget.toasts.process_failed"));
      }
    },
    [t, selectedMediaIndex],
  );

  const generate = useCallback(
    async (fromLanguage: string = "auto", toLanguage: string) => {
      if (state.type !== "uploaded") {
        logger.error(null, t("widget.toasts.no_file_uploaded"));
        return;
      }

      const { file, fileThumbnail, originalVideo, mode } = state;

      try {
        captureEvent(EventName.mw_translate_media_generation_started, {
          translate_media_mode: mode,
          source_lang: fromLanguage,
          target_lang: toLanguage,
        });
        // Update state to generating
        setState(prev => {
          if (prev.type !== "uploaded") return prev;
          return {
            type: "generating",
            mode: prev.mode,
            file: prev.file,
            fileThumbnail: prev.fileThumbnail,
            originalVideo: prev.originalVideo,
          };
        });
        // Upload and create dubbing project
        const url = new URL(`${SERVER_URL}/v1/dubbing-internal/anonymous`);
        const hcaptcha_token = await getCaptchaToken({ behaviourType: "translate-media" });

        const formData = new FormData();
        formData.append("mode", "automatic");
        formData.append("dubbing_studio", "true");
        formData.append("disable_voice_cloning", "false");
        formData.append("file", file);
        formData.append("name", file.name);
        formData.append("source_lang", fromLanguage);
        formData.append("target_lang", toLanguage);
        formData.append("num_speakers", "0");
        formData.append("highest_resolution", "true");
        formData.append("watermark", "false");
        formData.append("hcaptcha_token", hcaptcha_token);

        const response = await fetch(url.toString(), {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          if (error.detail.message === "quota_exceeded") {
            captureEvent(EventName.mw_translate_media_limit_shown, {
              limit_type: "quota",
              translate_media_mode: mode,
            });
            setState({
              type: "quota_exceeded",
              mode,
              originalVideo,
              file,
              fileThumbnail,
            });
            return;
          }
          throw new Error(error.detail.message || "Failed to upload file");
        }

        const data: DubbingResponse = await response.json();

        // Fetch transcripts and audio
        // We do it like this because backend generates the video lazily. If we just pass that url, the video will be blank for quite a long time without the user getting any indication that things are loading
        const translatedVideoBlob = await poll(
          async () => {
            const res = await fetch(`${SERVER_URL}/v1/dubbing/${data.dubbing_id}/audio/${toLanguage}/anonymous`);
            if (!res.ok) {
              if (res.status !== 425) {
                const error = await res.json();
                return { status: "error", message: getErrorMessageFromUnknownError(error) };
              }
              throw new Error("Too early");
            }
            return { status: "success", data: await res.blob() };
          },
          {
            intervalMs: 1000,
            timeoutMs: 10 * 60 * 1000, // ~10 minutes
            until: () => true,
          },
        );
        if (translatedVideoBlob.status === "error") {
          throw new Error(translatedVideoBlob.message);
        }

        const translatedAudioUrl = URL.createObjectURL(translatedVideoBlob.data!);

        // Update state to complete
        setState({
          type: "complete",
          mode,
          dubbing_id: data.dubbing_id,
          file,
          fileThumbnail,
          originalVideo: {
            videoUrl: originalVideo.videoUrl,
            color: originalVideo.color,
          },
          translatedVideo: {
            videoUrl: translatedAudioUrl,
            color: mode === "audio" ? pickColorSchemeFromSeed(file.name + "dubbed") : undefined,
          },
        });
        setSelectedMediaIndex(1);
        toast.success(t("widget.toasts.dubbing_success"));
        captureEvent(EventName.mw_translate_media_generation_completed, {
          translate_media_mode: mode,
          source_lang: fromLanguage,
          target_lang: toLanguage,
        });
      } catch (error) {
        toast.error(t("widget.toasts.dubbing_failed_title"), {
          description: error instanceof Error ? error.message : t("widget.toasts.dubbing_failed_description_fallback"),
        });
        // Reset to uploaded state on error
        setState({
          type: "uploaded",
          mode,
          file,
          fileThumbnail,
          originalVideo,
        });

        logger.error(error instanceof Error ? error : null, "Dubbing error");
        captureEvent(EventName.mw_translate_media_generation_error, {
          translate_media_mode: mode,
          source_lang: fromLanguage,
          target_lang: toLanguage,
        });
      }
    },
    [state, t],
  );

  const reset = useCallback(() => {
    setState({
      type: "demo",
      mode: initialMode,
      originalVideo,
      translatedVideo,
    });
    if (selectedMediaIndex !== 0) setSelectedMediaIndex(0);
  }, [originalVideo, translatedVideo, initialMode, selectedMediaIndex]);

  return {
    state,
    uploadVideo,
    generate,
    reset,
    selectedMediaIndex,
    setSelectedMediaIndex,
  };
}
